<script setup lang="ts">
import { computed, onMounted, ref, watch, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'
import { storeToRefs } from 'pinia'
import Menu from 'primevue/menu'
import Dialog from 'primevue/dialog'
import type { MenuItem } from 'primevue/menuitem'

import AppButton from '@/components/atoms/buttons/AppButton.vue'
import AppAICreditsFloatingButton from '@/components/molecules/ai/AppAICreditsFloatingButton.vue'
import MobileUserMenu from '@/components/atoms/sidebars/components/MobileUserMenu.vue'
import SidebarDrawer from '@/components/atoms/sidebars/components/SidebarDrawer.vue'
import { useSidebarRoutes } from '@/components/atoms/sidebars/composables/useSidebarRoutes'
import { useAuthStore } from '@/stores/useAuthStore'
import { useConnectivityStore } from '@/stores/useConnectivityStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { usePauseStatusStore } from '@/stores/usePauseStatusStore'
import type { ActivePauseSelection } from '@/stores/usePauseStatusStore'
import { usePauseCreateConfirmDialog } from '@/composables/usePauseCreateConfirmDialog'
import { useLogoutConfirm } from '@/components/atoms/sidebars/composables/useLogoutConfirm'
import { useCampaignRegistrationSync } from '@/composables/useCampaignRegistrationSync'
import { useWebphonePermissions } from '@/composables/useWebphonePermissions'
import { useCampaignRegistrationStore } from '@/stores/useCampaignRegistrationStore'
import { useCallService } from '@/services/call/useCallService'
import { useCampaignService } from '@/services/campaign/useCampaignService'
import { useChatService } from '@/services/chat/useChatService'
import { usePauseService } from '@/services/pause/usePauseService'
import { TypeCampaign } from '@/services/campaign/enums/type-campaign.enum'
import {
  CONNECTIVITY_PIP_CLOSE_EVENT,
  CONNECTIVITY_PIP_OPEN_EVENT,
} from '@/constants/connectivity-events'
import IconIncomingArrowCircle from '@/assets/svg/iconIncomingArrowCircle.svg'
import IconExitArrowCircle from '@/assets/svg/iconExitArrowCircle.svg'
import IconWaitingTime from '@/assets/svg/iconWaitingTime.svg'
import IconPhone from '@/assets/svg/icon-phone.svg'
import IconInfo from '@/assets/svg/IconInfo.svg'
import AppNotificationSettings from '@/components/molecules/notifications/AppNotificationSettings.vue'
import type {
  IOperatorSessionCall,
  IOperatorSessionCallUnregister,
  IRegisterOperatorSession,
  IRegisterQueues,
  IRegisterWhatsapp,
  IUnregisterOperatorSession,
  IUnregisterWhatsapp,
  OperatorSessionChannel,
} from '@/services/call/interfaces/IRegisterQueues'
import type { IOptionsCampaign } from '@/services/campaign/interfaces/options-campaign.interface'
import type { PauseDto } from '@/services/pause/interfaces/pause.interface'
import { LogOut, Menu as MenuIcon, X } from 'lucide-vue-next'

type CampaignExitMenuItem = MenuItem & {
  iconComponent: Component | string
}

const visible = ref(false)
const { routes } = useSidebarRoutes()
const { t } = useI18n()
const toast = useToast()
const { acceptLogout } = useLogoutConfirm()
const authStore = useAuthStore()
const connectivityStore = useConnectivityStore()
const workspace = useWorkspaceStore()
const pauseStatusStore = usePauseStatusStore()
const { confirmPauseCreation } = usePauseCreateConfirmDialog()
const campaignRegistrationStore = useCampaignRegistrationStore()
const callService = useCallService()
const campaignService = useCampaignService()
const chatService = useChatService()
const pauseService = usePauseService()
const { ensureMicrophoneAccess, shouldBlockWebphone } = useWebphonePermissions()
const { refreshChatRegistration, isVoiceRegistrationLost, isChatRegistrationLost } =
  useCampaignRegistrationSync()
const { isPaused, selectedPause, isSupervisorPause } = storeToRefs(pauseStatusStore)
const {
  isRegisteredInCampaign,
  isVoiceStateKnown,
  isVoiceRegistrationPending,
  isChatStatusLoading,
  isClassificationFormActive,
  canProcessScheduledCampaignExit,
} = storeToRefs(campaignRegistrationStore)
const pauseMenu = ref<InstanceType<typeof Menu> | null>(null)
const campaignExitMenu = ref<InstanceType<typeof Menu> | null>(null)
const pauses = ref<PauseDto[]>([])
const loadingAction = ref<'register' | 'unregister' | 'pause' | null>(null)
const isCampaignAlertDismissed = ref(false)
const isVoiceRegistrationAlertDismissed = ref(false)
const isChatRegistrationAlertDismissed = ref(false)
const confirmExitPauseVisible = ref(false)
const confirmScheduledCampaignExitVisible = ref(false)
const confirmExitPauseSource = ref<'pause' | 'campaign'>('pause')
const activeWebphoneStatuses = new Set(['incoming', 'calling', 'connected', 'held'])
const hasActiveWebphoneCall = computed(() =>
  activeWebphoneStatuses.has(workspace.webphoneCallStatus),
)

const extensionNumber = computed(() => authStore.extension?.number || 'N/A')
const extensionDescription = computed(() => authStore.extension?.description || '-')
const operatorCampaigns = computed<IOptionsCampaign[]>(() => {
  return workspace.campaigns
})

const hasVoiceCampaign = computed(() => workspace.hasVoiceCampaign)
// authStore.userExtension refleja la extensión efectiva de la sesión (propia o
// elegida en el paso 2 del login); vacía si el usuario entró sin extensión.
// hasVoiceCampaign no basta para esta comprobación: un usuario mixto (llamadas +
// WhatsApp) puede tener campaña de voz asignada y aun así no tener extensión.
const hasAssignedExtension = computed(() => Boolean(authStore.userExtension))

// "Cargando" mientras ningún canal tiene todavía un estado confirmado por su
// fuente: la consulta de chat en vuelo, la ventana entre el POST de registro de
// voz y el primer evento de Asterisk, o el arranque sin QueueMemberGrouped aún.
// Solo se espera al evento si puede llegar: sin campaña de voz o sin extensión
// el agente no es miembro de ninguna cola y Asterisk no lo va a reportar nunca.
const isCampaignStatusLoading = computed(
  () =>
    isChatStatusLoading.value ||
    isVoiceRegistrationPending.value ||
    (hasVoiceCampaign.value && hasAssignedExtension.value && !isVoiceStateKnown.value),
)
const isWebphoneInteractionDisabled = computed(
  () =>
    !authStore.webphoneAvailable ||
    !hasAssignedExtension.value ||
    authStore.extensionIsActive,
)
const isWebphoneDisconnected = computed(() => connectivityStore.isAnyServiceDisconnected)
const shouldShowWebphoneDisconnectedStyle = computed(
  () =>
    isWebphoneInteractionDisabled.value ||
    isWebphoneDisconnected.value ||
    shouldBlockWebphone.value,
)
const isRegisterActionDisabled = computed(
  () => isCampaignStatusLoading.value || loadingAction.value === 'register',
)

const handleWebphoneClick = () => {
  if (isWebphoneInteractionDisabled.value) return

  if (isWebphoneDisconnected.value) {
    if (authStore.showWebphone) {
      authStore.setShowWebphone(false)
    }
    return
  }

  if (hasActiveWebphoneCall.value) {
    authStore.setShowWebphone(true)
    workspace.toggleWebphonePipExpanded()
    return
  }

  authStore.toggleWebphone()
}

// Sin extensión asignada, el usuario no puede atender campañas de llamadas: se
// excluyen del registro para que "Iniciar campaña" solo registre WhatsApp
// (regla de negocio de extensiones — ver getRegisterQueuePayload/enterCampaign).
const queueCampaigns = computed(() => {
  if (!hasAssignedExtension.value) return []

  return operatorCampaigns.value
    .filter((campaign) => campaign.type !== TypeCampaign.CHAT)
    .filter((campaign) => Boolean(campaign.id))
    .map((campaign) => ({
      id: campaign.id,
      queue: campaign.queue ?? '',
    }))
})

const whatsappCampaignIds = computed(() =>
  operatorCampaigns.value
    .filter((campaign) => campaign.type === TypeCampaign.CHAT)
    .map((campaign) => campaign.id)
    .filter(Boolean),
)

watch(isRegisteredInCampaign, (registered) => {
  if (registered) isCampaignAlertDismissed.value = false
})

watch(isVoiceRegistrationLost, (lost) => {
  if (!lost) isVoiceRegistrationAlertDismissed.value = false
})

watch(isChatRegistrationLost, (lost) => {
  if (!lost) isChatRegistrationAlertDismissed.value = false
})

const currentPauseLabel = computed(() => {
  if (!isPaused.value) return t('header.actions.pause')

  const base = pauseStatusStore.activePauseLabel
    ? `${t('call.exit_pause')}: ${pauseStatusStore.activePauseLabel}`
    : t('call.active_pause')

  return pauseStatusStore.pauseElapsedLabel
    ? `${base} (${pauseStatusStore.pauseElapsedLabel})`
    : base
})

const pausedColor = '#FFD900'
const pausedHeaderShadow = computed(() =>
  isPaused.value
    ? '0 0 0 1px rgba(255, 217, 0, 0.28), 0 6px 18px rgba(255, 217, 0, 0.16)'
    : undefined,
)

const showHeaderToast = (severity: 'success' | 'info' | 'warn' | 'error', detail: string) => {
  toast.add({
    severity,
    summary: t(
      severity === 'success'
        ? 'general.success'
        : severity === 'error'
          ? 'general.error'
          : 'general.warning',
    ),
    detail,
    life: 3000,
  })
}

const refreshWhatsAppChatsAfterCampaignRegister = async () => {
  if (!authStore.userId || !workspace.hasWhatsAppCampaign) return

  try {
    const chatsResponse = await chatService.getChats({
      user_id: authStore.userId,
    })

    window.dispatchEvent(
      new CustomEvent('whatsapp-campaign-registered', {
        detail: { chatsResponse },
      }),
    )
  } catch {}
}

const loadCampaigns = async () => {
  if (workspace.hasLoadedCampaigns) return

  const campaigns = await campaignService.getAllCampaigns()
  workspace.setCampaigns(Array.isArray(campaigns) ? campaigns : [])
}

const getSessionIdentifier = () =>
  localStorage.getItem('oper_session_identifier') ||
  authStore.currentUser?.email ||
  authStore.userAlias ||
  ''

const notifyWhatsAppOnlyRegistration = () => {
  showHeaderToast('info', t('call.whatsapp_only_registration'))
}

const validateVoiceCampaignPrerequisites = async (canRegisterWhatsapp: boolean) => {
  if (!hasVoiceCampaign.value) return true

  if (!hasAssignedExtension.value || !authStore.webphoneAvailable) {
    if (canRegisterWhatsapp) {
      notifyWhatsAppOnlyRegistration()
      return false
    }

    showHeaderToast('warn', t('call.extension_session_required'))
    return false
  }

  try {
    const hasActiveSession = await authStore.validateActiveSession(getSessionIdentifier(), {
      force: true,
    })

    if (!hasActiveSession) {
      if (canRegisterWhatsapp) {
        notifyWhatsAppOnlyRegistration()
        return false
      }

      showHeaderToast('warn', t('call.extension_session_required'))
      return false
    }
  } catch {
    if (canRegisterWhatsapp) {
      notifyWhatsAppOnlyRegistration()
      return false
    }

    showHeaderToast('error', t('auth.session_check_error'))
    return false
  }

  const microphoneAccess = await ensureMicrophoneAccess()
  if (microphoneAccess.allowed) return true

  if (canRegisterWhatsapp) {
    notifyWhatsAppOnlyRegistration()
    return false
  }

  const messageKey =
    microphoneAccess.status === 'denied'
      ? 'call.microphone_access_required'
      : 'call.microphone_device_required'

  showHeaderToast('warn', t(messageKey))
  return false
}

const loadPauses = async () => {
  const result = await pauseService.getPauses({ page: 1, size: 100 })
  pauses.value = result.data ?? []
  pauseStatusStore.hydrateFromCatalog(pauses.value)
}

const getRegisterQueuePayload = (): IRegisterQueues | null => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return null
  }

  return {
    user_id: authStore.userId,
    protocol: 'PJSIP',
    extension: authStore.userExtension || extensionNumber.value,
    alias: authStore.userAlias || authStore.userName || extensionDescription.value,
    campaign_ids: queueCampaigns.value,
  }
}

const getOperatorSessionCallPayload = (): IOperatorSessionCall | null => {
  const queuePayload = getRegisterQueuePayload()
  if (!queuePayload) return null

  return {
    protocol: queuePayload.protocol,
    extension: queuePayload.extension,
    alias: queuePayload.alias,
    campaign_ids: queuePayload.campaign_ids,
  }
}

const getOperatorSessionCallUnregisterPayload = (): IOperatorSessionCallUnregister | null => {
  const queuePayload = getRegisterQueuePayload()
  if (!queuePayload) return null

  return {
    protocol: queuePayload.protocol,
    extension: queuePayload.extension,
    campaign_ids: queuePayload.campaign_ids,
  }
}

const getRegisterWhatsappPayload = (): IRegisterWhatsapp | null => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return null
  }

  if (!whatsappCampaignIds.value.length) {
    return null
  }

  return {
    user_id: authStore.userId,
    campaign_ids: whatsappCampaignIds.value,
  }
}

const getUnregisterWhatsappPayload = (): IUnregisterWhatsapp | null => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return null
  }

  if (!whatsappCampaignIds.value.length) {
    return null
  }

  return {
    user_id: authStore.userId,
  }
}

const getRegisteredCampaignIds = (
  callPayload: IOperatorSessionCall | IOperatorSessionCallUnregister | null,
  whatsappCampaignIds: string[] = [],
) => {
  return Array.from(
    new Set([
      ...(callPayload?.campaign_ids.map((campaign) => campaign.id) ?? []),
      ...whatsappCampaignIds,
    ]),
  )
}

const syncRegisteredCampaignUseIa = (campaignIds: string[]) => {
  workspace.setUseIa(campaignIds.some((campaignId) => workspace.getCampaignUseIa(campaignId)))
}

const clearRegisteredCampaignUseIa = (campaignIds: string[]) => {
  campaignIds.forEach((campaignId) => {
    workspace.setCampaignUseIa(campaignId, false)
  })
  workspace.setUseIa(false)
}

const enterCampaign = async () => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return
  }

  const whatsappPayload = getRegisterWhatsappPayload()
  const canRegisterWhatsapp = Boolean(whatsappPayload)
  let canRegisterVoice = false
  try {
    canRegisterVoice = await validateVoiceCampaignPrerequisites(canRegisterWhatsapp)
  } catch {
    window.dispatchEvent(new Event(CONNECTIVITY_PIP_CLOSE_EVENT))
    showHeaderToast('error', t('call.error_register_queues'))
    return
  }
  const callPayload =
    canRegisterVoice && queueCampaigns.value.length ? getOperatorSessionCallPayload() : null

  // El registro solo se envia si al menos un canal puede iniciar sesion.
  // Si la telefonia falla pero hay WhatsApp, se registra exclusivamente WhatsApp.
  if (!callPayload && !whatsappPayload) {
    window.dispatchEvent(new Event(CONNECTIVITY_PIP_CLOSE_EVENT))
    showHeaderToast('warn', t('call.no_campaign_channels_available'))
    return
  }

  loadingAction.value = 'register'
  try {
    const payload: IRegisterOperatorSession = {
      user_id: authStore.userId,
      ...(callPayload ? { call: callPayload } : {}),
      ...(whatsappPayload ? { whatsapp: { campaign_ids: whatsappPayload.campaign_ids } } : {}),
    }

    const result = await callService.registerOperatorSession(payload, { notify: false })
    const failedChannels = result.failed_channels ?? []

    syncRegisteredCampaignUseIa(
      getRegisteredCampaignIds(callPayload, whatsappPayload?.campaign_ids ?? []),
    )
    // El registro de voz no se da por hecho acá: se marca como pendiente y lo
    // confirma el primer QueueMemberGrouped que publique el poll de colas.
    if (callPayload && !failedChannels.includes('CALL')) {
      campaignRegistrationStore.markVoiceRegistrationPending()
    }
    window.dispatchEvent(new Event(CONNECTIVITY_PIP_OPEN_EVENT))

    // Flujo anterior, reemplazado por /oper/operator-session/register.
    // await Promise.allSettled([
    //   queuePayload
    //     ? callService.registerQueues(queuePayload, { notify: false })
    //     : Promise.resolve(true),
    //   whatsappPayload
    //     ? callService.registerWhatsapp(whatsappPayload, { notify: false })
    //     : Promise.resolve(true),
    // ])

    if (whatsappPayload && !failedChannels.includes('WHATSAPP')) {
      await refreshWhatsAppChatsAfterCampaignRegister()
    }
    await refreshChatRegistration()
    notifyRegistrationOutcome(failedChannels)
  } catch {
    window.dispatchEvent(new Event(CONNECTIVITY_PIP_CLOSE_EVENT))
    // El backend registra por cola y persiste las que sí entraron antes de
    // fallar: se vuelve a preguntar en vez de asumir que no quedó nada.
    await refreshChatRegistration()
    showHeaderToast('error', t('call.error_register_queues'))
  } finally {
    loadingAction.value = null
  }
}

/**
 * El registro no es todo-o-nada: el backend responde 200 mientras al menos un
 * canal haya entrado, con los que fallaron en `failed_channels`. Se avisa qué
 * quedó fuera en vez de un "error al entrar a campaña" que contradiría al canal
 * que sí quedó registrado.
 */
const notifyRegistrationOutcome = (failedChannels: OperatorSessionChannel[]) => {
  if (!failedChannels.length) {
    showHeaderToast('success', t('call.success_register_queues'))
    return
  }

  showHeaderToast(
    'warn',
    t(
      failedChannels.includes('CALL')
        ? 'call.partial_registration_voice_failed'
        : 'call.partial_registration_chat_failed',
    ),
  )
}

/**
 * Re-registra SOLO el canal de chat, sin tocar la voz: el backend registra CALL
 * y luego WHATSAPP, así que un fallo en el segundo deja al agente dentro de las
 * colas y fuera de sus campañas de WhatsApp.
 */
const retryChatRegistration = async () => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return
  }

  const whatsappPayload = getRegisterWhatsappPayload()
  if (!whatsappPayload) {
    showHeaderToast('warn', t('call.no_campaign_channels_available'))
    return
  }

  loadingAction.value = 'register'
  try {
    await callService.registerOperatorSession(
      { user_id: authStore.userId, whatsapp: { campaign_ids: whatsappPayload.campaign_ids } },
      { notify: false },
    )
    // A diferencia de la voz, acá no hay heartbeat que confirme: campaign_logins
    // es la fuente y se puede leer de inmediato.
    await refreshWhatsAppChatsAfterCampaignRegister()
    await refreshChatRegistration()
    showHeaderToast('success', t('call.success_register_queues'))
  } catch {
    await refreshChatRegistration()
    showHeaderToast('error', t('call.error_register_queues'))
  } finally {
    loadingAction.value = null
  }
}

/**
 * Re-registra SOLO el canal de voz, sin tocar el chat: se usa cuando Asterisk
 * dejó de reportar al agente pero su campaign_login sigue abierto, estado en el
 * que "Iniciar campaña" no aparece porque el chat lo sigue dando por registrado.
 */
const retryVoiceRegistration = async () => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return
  }

  // Sin fallback a WhatsApp: acá el chat ya está registrado, así que los avisos
  // deben hablar de lo que falta (extensión, sesión, micrófono).
  const canRegisterVoice = await validateVoiceCampaignPrerequisites(false)
  if (!canRegisterVoice) return

  const callPayload = queueCampaigns.value.length ? getOperatorSessionCallPayload() : null
  if (!callPayload) {
    showHeaderToast('warn', t('call.no_campaign_channels_available'))
    return
  }

  loadingAction.value = 'register'
  try {
    await callService.registerOperatorSession(
      { user_id: authStore.userId, call: callPayload },
      { notify: false },
    )
    // No se sincroniza el flag de IA: ya se resolvió en el registro original y
    // recalcularlo solo con las campañas de voz pisaría el valor de las de chat.
    campaignRegistrationStore.markVoiceRegistrationPending()
    showHeaderToast('success', t('call.success_register_queues'))
  } catch {
    showHeaderToast('error', t('call.error_register_queues'))
  } finally {
    loadingAction.value = null
  }
}

const executeCampaignExit = async (isScheduledExit = false): Promise<boolean> => {
  if (hasActiveWebphoneCall.value) {
    if (!isScheduledExit) confirmScheduledCampaignExitVisible.value = true
    return false
  }

  if (isPaused.value) {
    confirmExitPauseSource.value = 'campaign'
    confirmExitPauseVisible.value = true
    if (isScheduledExit) campaignRegistrationStore.resolveScheduledCampaignExit(false)
    return false
  }

  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    if (isScheduledExit) campaignRegistrationStore.resolveScheduledCampaignExit(false)
    return false
  }

  const callPayload = queueCampaigns.value.length ? getOperatorSessionCallUnregisterPayload() : null
  const whatsappPayload = getUnregisterWhatsappPayload()

  loadingAction.value = 'unregister'
  let campaignExitCompleted = false
  try {
    const payload: IUnregisterOperatorSession = {
      user_id: authStore.userId,
      ...(callPayload ? { call: callPayload } : {}),
      ...(whatsappPayload ? { whatsapp: { enabled: true } } : {}),
    }

    await callService.unregisterOperatorSession(payload, { notify: false })
    clearRegisteredCampaignUseIa(
      getRegisteredCampaignIds(callPayload, whatsappPayload ? whatsappCampaignIds.value : []),
    )

    // Flujo anterior, reemplazado por /oper/operator-session/unregister.
    // await Promise.allSettled([
    //   queuePayload
    //     ? callService.unregisterQueues(queuePayload, { notify: false })
    //     : Promise.resolve(true),
    //   whatsappPayload
    //     ? callService.unregisterWhatsapp(whatsappPayload, { notify: false })
    //     : Promise.resolve(true),
    // ])

    // La baja de voz la confirma la ausencia del evento: si el QueueRemove no
    // prosperó en Asterisk el agente sigue en la cola y el heartbeat lo va a
    // seguir reportando como registrado, que es la verdad.
    campaignRegistrationStore.expectVoiceUnregistration()
    window.dispatchEvent(new Event(CONNECTIVITY_PIP_CLOSE_EVENT))
    pauseStatusStore.clearPause()
    await refreshChatRegistration()
    campaignExitCompleted = true
    showHeaderToast('success', t('call.success_unregister_queues'))
  } catch {
    await refreshChatRegistration()
    showHeaderToast('error', t('call.error_unregister_queues'))
  } finally {
    loadingAction.value = null
    if (isScheduledExit) {
      campaignRegistrationStore.resolveScheduledCampaignExit(campaignExitCompleted)
    }
  }

  return campaignExitCompleted
}

const exitCampaign = () => executeCampaignExit()

const exitCampaignAndLogout = async () => {
  const campaignExitCompleted = await executeCampaignExit()
  if (campaignExitCompleted) await acceptLogout()
}

const toggleCampaignExitMenu = (event: MouseEvent) => {
  campaignExitMenu.value?.toggle(event)
}

watch(
  [hasActiveWebphoneCall, canProcessScheduledCampaignExit],
  ([hasActiveCall, canProcessExit]) => {
    if (hasActiveCall || !canProcessExit || !campaignRegistrationStore.claimScheduledCampaignExit())
      return

    void executeCampaignExit(true)
  },
  { flush: 'post' },
)

const getActivePause = async (): Promise<ActivePauseSelection | null> => {
  if (selectedPause.value) return selectedPause.value

  // Solo persiste el id entre recargas: se resuelve contra el catálogo.
  if (!pauses.value.length) {
    await loadPauses()
  }

  return selectedPause.value
}

const setQueuePause = async (pause: ActivePauseSelection, pauseState: boolean) => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return
  }

  if (pauseState) {
    try {
      await confirmPauseCreation()
    } catch {
      return
    }
  }

  loadingAction.value = 'pause'
  try {
    const result = await callService.pauseQueues({
      user_id: authStore.userId,
      pause: {
        id: pause.id,
        name: pause.name,
        description: pause.description,
      },
      is_paused: pauseState,
      extension: authStore.userExtension || extensionNumber.value,
      protocol: 'PJSIP',
    })

    if (!result.status) return

    if (pauseState) {
      pauseStatusStore.applySelfPause(pause)
    } else {
      pauseStatusStore.clearPause()
    }
  } finally {
    loadingAction.value = null
  }
}

const exitPause = async () => {
  // Único motivo de bloqueo: el origen de la pausa, que sale del PausedReason que
  // reporta Asterisk. Antes se bloqueaba por no tener la pausa en memoria, y eso
  // también pasa con pausas propias (recarga, otro navegador, POST que no volvió),
  // así que el agente no podía reanudar la suya.
  if (isSupervisorPause.value) {
    showHeaderToast('warn', t('call.supervisor_pause_locked'))
    return
  }

  // Reanudar no necesita la pausa de catálogo: el backend cierra el pause_log
  // abierto del usuario sin mirar el id y manda Reason vacío a Asterisk. Se envía
  // igual aunque no se haya podido resolver cuál era —el botón solo se pulsa
  // estando pausado y despausar es idempotente—: quedarse sin mandarlo dejaba al
  // agente pausado en Asterisk sin salida.
  const activePause = await getActivePause()
  await setQueuePause(activePause ?? { id: '', name: '', description: '' }, false)
}

const togglePauseMenu = async (event: MouseEvent) => {
  // Pausar exige estar en campaña; salir de una pausa abierta, no: sin registro
  // el agente no tendría forma de cerrarla.
  if (!isRegisteredInCampaign.value && !isPaused.value) return

  if (isPaused.value) {
    await exitPause()
    return
  }

  if (!pauses.value.length) {
    await loadPauses()
  }

  pauseMenu.value?.toggle(event)
}

const pauseMenuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = pauses.value.map((pause) => ({
    label: pause.name,
    command: () => void setQueuePause(pause, true),
  }))

  if (!items.length) {
    return [{ label: t('call.no_pauses_available'), disabled: true }]
  }

  return items
})

const campaignExitMenuItems = computed<CampaignExitMenuItem[]>(() => [
  {
    label: t('header.actions.end_campaign'),
    iconComponent: IconExitArrowCircle,
    command: () => void exitCampaign(),
  },
  {
    label: t('header.actions.end_campaign_and_logout'),
    iconComponent: LogOut,
    command: () => void exitCampaignAndLogout(),
  },
])

const confirmExitPauseTitle = computed(() =>
  confirmExitPauseSource.value === 'campaign'
    ? t('call.confirm_exit_pause_campaign_title')
    : t('call.confirm_exit_pause_title'),
)

const confirmExitPauseMessage = computed(() =>
  confirmExitPauseSource.value === 'campaign'
    ? t('call.confirm_exit_pause_from_campaign_message')
    : t('call.confirm_exit_pause_message'),
)

const cancelExitPause = () => {
  confirmExitPauseVisible.value = false
}

const cancelScheduledCampaignExit = () => {
  confirmScheduledCampaignExitVisible.value = false
}

const confirmScheduledCampaignExit = () => {
  campaignRegistrationStore.scheduleCampaignExit(isClassificationFormActive.value)
  confirmScheduledCampaignExitVisible.value = false
  showHeaderToast('info', t('call.campaign_exit_scheduled'))
}

const confirmExitPause = async () => {
  await exitPause()
  confirmExitPauseVisible.value = false
}

onMounted(() => {
  void loadCampaigns()
  void loadPauses()
  void refreshChatRegistration()
})
</script>

<template>
  <div class="w-full flex flex-col">
    <div
      class="w-full app-card-glass backdrop-blur-2xl! backdrop-saturate-150 flex items-center justify-between p-2 shadow-sm"
      :style="{ boxShadow: pausedHeaderShadow }"
    >
      <div class="flex shrink-0 items-center justify-start gap-1">
        <button
          type="button"
          class="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-[var(--p-primary-color)] transition hover:bg-[var(--p-surface-100)] active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--p-primary-color)] dark:text-[var(--p-primary-color)] dark:hover:bg-[var(--p-surface-800)]"
          @click.stop="visible = true"
        >
          <MenuIcon :size="22" :stroke-width="2" />
        </button>

        <button
          type="button"
          class="relative flex h-10 w-12 shrink-0 items-center justify-center transition active:scale-95"
          :class="
            isWebphoneInteractionDisabled ? 'cursor-not-allowed opacity-50' : 'hover:scale-105'
          "
          @click="handleWebphoneClick"
        >
          <IconPhone
            class="h-9 w-11"
            :stroke-width="2"
            :class="
              shouldShowWebphoneDisconnectedStyle
                ? 'text-gray-400'
                : 'text-[var(--p-primary-color)]'
            "
          />
          <span
            class="montserrat absolute inset-0 ml-5 flex items-center justify-center text-[8px] font-semibold leading-none"
            :class="
              shouldShowWebphoneDisconnectedStyle
                ? 'text-gray-400'
                : 'text-[var(--p-primary-color)]'
            "
          >
            {{ extensionNumber }}
          </span>
        </button>
      </div>

      <div class="flex shrink-0 items-center justify-end gap-1">
        <AppButton
          v-if="!isRegisteredInCampaign && isPaused"
          v-tooltip.bottom="t('call.exit_pause')"
          severity="secondary"
          size="small"
          outlined
          rounded
          :block="false"
          :disabled="loadingAction === 'pause'"
          :loading="loadingAction === 'pause'"
          class="!h-9 !w-9 !min-w-9 !p-0"
          :style="{ color: pausedColor, borderColor: pausedColor }"
          @click="togglePauseMenu"
        >
          <template #icon-start>
            <IconWaitingTime class="h-5 w-5" :stroke-width="1.5" />
          </template>
        </AppButton>

        <AppButton
          v-if="!isRegisteredInCampaign"
          v-tooltip.bottom="t('header.actions.start_campaign')"
          severity="secondary"
          size="small"
          outlined
          rounded
          :block="false"
          :disabled="isRegisterActionDisabled"
          :loading="isCampaignStatusLoading || loadingAction === 'register'"
          class="!h-9 !w-9 !min-w-9 !p-0"
          :style="{ color: '#DA002C', borderColor: '#DA002C' }"
          @click="enterCampaign"
        >
          <template #icon-start>
            <IconIncomingArrowCircle class="h-5 w-5" :stroke-width="1.5" />
          </template>
        </AppButton>

        <template v-else>
          <AppButton
            v-tooltip.bottom="currentPauseLabel"
            severity="secondary"
            size="small"
            outlined
            rounded
            :block="false"
            :disabled="isCampaignStatusLoading || loadingAction === 'pause'"
            :loading="loadingAction === 'pause'"
            class="!h-9 !w-9 !min-w-9 !p-0"
            :style="{
              color: isPaused ? pausedColor : '#9770E5',
              borderColor: isPaused ? pausedColor : '#9770E5',
            }"
            @click="togglePauseMenu"
          >
            <template #icon-start>
              <IconWaitingTime class="h-5 w-5" :stroke-width="1.5" />
            </template>
          </AppButton>

          <AppButton
            v-tooltip.bottom="t('header.actions.end_campaign')"
            severity="secondary"
            size="small"
            outlined
            rounded
            :block="false"
            :disabled="isCampaignStatusLoading || loadingAction === 'unregister'"
            :loading="loadingAction === 'unregister'"
            class="!h-9 !w-9 !min-w-9 !p-0"
            :style="{ color: '#5AC2E8', borderColor: '#5AC2E8' }"
            @click="toggleCampaignExitMenu"
          >
            <template #icon-start>
              <IconExitArrowCircle class="h-5 w-5" :stroke-width="1.5" />
            </template>
          </AppButton>
        </template>

        <!-- <AppNotificationSettings compact />
        <AppAICreditsFloatingButton compact allow-purchase /> -->
        <MobileUserMenu />
      </div>
    </div>

    <div
      v-if="!isRegisteredInCampaign && !isCampaignAlertDismissed"
      class="mt-1.5 flex flex-col items-stretch gap-2 rounded-lg border border-amber-400/35 bg-amber-500/10 px-3 py-2 text-xs transition-all duration-200 backdrop-blur-2xl! backdrop-saturate-150 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-3.5 sm:py-1.5 dark:border-amber-500/25 dark:bg-amber-500/10"
      role="status"
    >
      <div class="flex min-w-0 flex-wrap items-center gap-2">
        <span
          class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-700 dark:bg-amber-400/20 dark:text-amber-300"
        >
          <IconInfo class="h-3.5 w-3.5" />
        </span>
        <span class="font-semibold text-amber-950 dark:text-amber-100">{{
          t('call.not_registered_campaign_title')
        }}</span>
        <span class="min-w-0 text-amber-900/80 dark:text-amber-200/80">
          {{ t('call.campaign_registration_prompt_link')
          }}{{ t('call.campaign_registration_prompt_after') }}
        </span>
      </div>
      <div class="flex w-full shrink-0 items-center gap-2 sm:w-auto">
        <button
          type="button"
          class="min-w-0 flex-1 cursor-pointer whitespace-normal rounded-md bg-[var(--p-primary-color)] px-2.5 py-1 text-center text-xs font-semibold leading-tight text-white shadow-xs transition hover:opacity-90 active:scale-95 disabled:cursor-wait disabled:opacity-60 sm:flex-none"
          :disabled="isCampaignStatusLoading || loadingAction === 'register'"
          @click="enterCampaign"
        >
          {{ t('call.register_now') }}
        </button>
        <button
          type="button"
          class="cursor-pointer flex h-6 w-6 items-center justify-center rounded-md text-amber-800/70 transition hover:bg-amber-500/20 hover:text-amber-950 dark:text-amber-300/70 dark:hover:bg-amber-500/20 dark:hover:text-amber-100"
          :aria-label="t('call.dismiss_notification')"
          @click="isCampaignAlertDismissed = true"
        >
          <X :size="16"></X>
        </button>
      </div>
    </div>

    <div
      v-if="isVoiceRegistrationLost && !isVoiceRegistrationAlertDismissed"
      class="mt-1.5 flex flex-col items-stretch gap-2 rounded-lg border border-amber-400/35 bg-amber-500/10 px-3 py-2 text-xs transition-all duration-200 backdrop-blur-2xl! backdrop-saturate-150 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-3.5 sm:py-1.5 dark:border-amber-500/25 dark:bg-amber-500/10"
      role="status"
    >
      <div class="flex min-w-0 items-center gap-2 sm:flex-1">
        <span
          class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-700 dark:bg-amber-400/20 dark:text-amber-300"
          ><IconInfo class="h-3.5 w-3.5" />
        </span>
        <span class="font-medium text-amber-950 dark:text-amber-100">
          {{t('call.voice_registration_lost')}}
        </span>
      </div>
      <div class="flex w-full shrink-0 items-center gap-2 sm:w-auto">
        <button
          type="button"
          class="min-w-0 flex-1 cursor-pointer whitespace-normal rounded-md bg-[var(--p-primary-color)] px-2.5 py-1 text-center text-xs font-semibold leading-tight text-white shadow-xs transition hover:opacity-90 active:scale-95 disabled:cursor-wait disabled:opacity-60 sm:flex-none"
          :disabled="loadingAction === 'register'"
          @click="retryVoiceRegistration"
        >
          {{ t('call.retry_voice_registration') }}
        </button>
        <button
          type="button"
          class="cursor-pointer flex h-6 w-6 items-center justify-center rounded-md text-amber-800/70 transition hover:bg-amber-500/20 hover:text-amber-950 dark:text-amber-300/70 dark:hover:bg-amber-500/20 dark:hover:text-amber-100"
          :aria-label="t('call.dismiss_notification')"
          @click="isVoiceRegistrationAlertDismissed = true"
        >
          <X :size="16"></X>
        </button>
      </div>
    </div>

    <div
      v-else-if="isChatRegistrationLost && !isChatRegistrationAlertDismissed"
      class="mt-1.5 flex flex-col items-stretch gap-2 rounded-lg border border-amber-400/35 bg-amber-500/10 px-3 py-2 text-xs transition-all duration-200 backdrop-blur-2xl! backdrop-saturate-150 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-3.5 sm:py-1.5 dark:border-amber-500/25 dark:bg-amber-500/10"
      role="status"
    >
      <div class="flex min-w-0 items-center gap-2">
        <span
          class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-700 dark:bg-amber-400/20 dark:text-amber-300"
        >
          <IconInfo class="h-3.5 w-3.5" />
        </span>
        <span class="font-medium text-amber-950 dark:text-amber-100"
          >{{ t('call.chat_registration_lost') }}
        </span>
      </div>
      <div class="flex w-full shrink-0 items-center gap-2 sm:w-auto">
        <button
          type="button"
          class="min-w-0 flex-1 cursor-pointer whitespace-normal rounded-md bg-[var(--p-primary-color)] px-2.5 py-1 text-center text-xs font-semibold leading-tight text-white shadow-xs transition hover:opacity-90 active:scale-95 disabled:cursor-wait disabled:opacity-60 sm:flex-none"
          :disabled="loadingAction === 'register'"
          @click="retryChatRegistration"
        >
          {{ t('call.retry_chat_registration') }}
        </button>
        <button
          type="button"
          class="cursor-pointer flex h-6 w-6 items-center justify-center rounded-md text-amber-800/70 transition hover:bg-amber-500/20 hover:text-amber-950 dark:text-amber-300/70 dark:hover:bg-amber-500/20 dark:hover:text-amber-100"
          :aria-label="t('call.dismiss_notification')"
          @click="isChatRegistrationAlertDismissed = true"
        >
          <X :size="16"></X>
        </button>
      </div>
    </div>

    <Menu
      ref="pauseMenu"
      :model="pauseMenuItems"
      :popup="true"
      class="!rounded-lg !border !border-white/40 !bg-white/20 !shadow-xl !backdrop-blur-xl !backdrop-saturate-150 dark:!border-[var(--p-primary-color)]/30 dark:!bg-slate-900/40 [&_.p-menu-list]:!bg-transparent [&_.p-menu-item-content]:!bg-transparent [&_.p-menu-item-content:hover]:!bg-[var(--p-primary-color)]/12 [&_.p-menu-item-icon]:!text-[var(--p-text-color)] dark:[&_.p-menu-item-icon]:!text-[var(--p-primary-50)] [&_.p-menu-item-label]:!text-[var(--p-text-color)] dark:[&_.p-menu-item-label]:!text-[var(--p-primary-50)]"
    />
    <Menu
      ref="campaignExitMenu"
      :model="campaignExitMenuItems"
      :popup="true"
      class="!rounded-lg !border !border-white/40 !bg-white/20 !shadow-xl !backdrop-blur-xl !backdrop-saturate-150 dark:!border-[var(--p-primary-color)]/30 dark:!bg-slate-900/40 [&_.p-menu-list]:!bg-transparent [&_.p-menu-item-content]:!bg-transparent [&_.p-menu-item-content:hover]:!bg-[var(--p-primary-color)]/12 [&_.p-menu-item-icon]:!text-[var(--p-text-color)] dark:[&_.p-menu-item-icon]:!text-[var(--p-primary-50)] [&_.p-menu-item-label]:!text-[var(--p-text-color)] dark:[&_.p-menu-item-label]:!text-[var(--p-primary-50)]"
    >
      <template #item="{ item, props }">
        <a
          v-bind="props.action"
          class="flex items-center gap-2 text-[var(--p-text-color)] dark:text-[var(--p-primary-50)]"
        >
          <component :is="item.iconComponent" class="h-4 w-4" :stroke-width="1.5" />
          <span class="text-[var(--p-text-color)] dark:text-[var(--p-primary-50)]">{{
            item.label
          }}</span>
        </a>
      </template>
    </Menu>
    <SidebarDrawer v-model:visible="visible" :routes="routes" />

    <Dialog
      v-model:visible="confirmExitPauseVisible"
      modal
      :header="confirmExitPauseTitle"
      :style="{ width: 'min(28rem, calc(100vw - 2rem))' }"
      :draggable="false"
    >
      <p class="m-0 text-sm leading-6 text-gray-700 dark:text-gray-200">
        {{ confirmExitPauseMessage }}
      </p>

      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton
            :label="t('general.cancel')"
            variant="secondary"
            outlined
            :block="false"
            @click="cancelExitPause"
          />
          <AppButton
            :label="t('call.exit_pause')"
            variant="danger"
            :block="false"
            :loading="loadingAction === 'pause'"
            @click="confirmExitPause"
          />
        </div>
      </template>
    </Dialog>

    <Dialog
      v-model:visible="confirmScheduledCampaignExitVisible"
      modal
      :header="t('call.confirm_scheduled_campaign_exit_title')"
      :style="{ width: 'min(28rem, calc(100vw - 2rem))' }"
      :draggable="false"
    >
      <p class="m-0 text-sm leading-6 text-gray-700 dark:text-gray-200">
        {{ t('call.confirm_scheduled_campaign_exit_message') }}
      </p>

      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton
            :label="t('general.cancel')"
            variant="secondary"
            outlined
            :block="false"
            @click="cancelScheduledCampaignExit"
          />
          <AppButton
            :label="t('call.confirm_scheduled_campaign_exit_continue')"
            variant="danger"
            :block="false"
            @click="confirmScheduledCampaignExit"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
